import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  FiX,
  FiChevronDown,
  FiCheck,
  FiShield,
  FiUser,
  FiClock,
  FiBookOpen,
  FiRefreshCw,
  FiAlertCircle,
  FiSearch,
  FiGrid,
  FiCheckCircle,
} from "react-icons/fi";
import api from "../../../connector/api";

// Helper to reliably extract IDs from objects or primitive values
const getId = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object" && val.id !== undefined) return String(val.id);
  return String(val);
};

// --- TOAST NOTIFICATION COMPONENT ---
function ToastNotification({ toast, onClose }) {
  if (!toast) return null;

  const isSuccess = toast.type === "success";

  return ReactDOM.createPortal(
    <div
      className={`fixed top-5 right-5 z-[100000] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-lg text-white text-xs font-medium transition-all duration-200 animate-in slide-in-from-top-2 ${
        isSuccess ? "bg-emerald-600" : "bg-red-600"
      }`}
    >
      {isSuccess ? (
        <FiCheckCircle className="w-4 h-4 shrink-0 text-white" />
      ) : (
        <FiAlertCircle className="w-4 h-4 shrink-0 text-white" />
      )}
      <span className="truncate max-w-xs">{toast.message}</span>
      <button
        type="button"
        onClick={onClose}
        className="ml-2 text-white/80 hover:text-white transition-colors"
      >
        <FiX className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
}

// --- CUSTOM SINGLE PORTAL DROPDOWN ---
function CustomDropdown({ label, value, options, onChange, icon: Icon, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption =
    options.find((opt) => String(opt.value) === String(value)) || options[0];

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 160),
      });
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-dropdown-menu")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium ${
          disabled ? "opacity-50 cursor-not-allowed bg-zinc-100" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />}
          <span className="truncate text-zinc-700">{selectedOption?.label}</span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-dropdown-menu fixed bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-[99999] animate-in fade-in zoom-in-95 duration-100 max-h-56 overflow-y-auto custom-scroll"
          >
            {label && (
              <div className="px-3 py-1 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                {label}
              </div>
            )}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors text-xs cursor-pointer ${
                  String(value) === String(option.value)
                    ? "bg-zinc-100 text-zinc-900 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <span className="truncate">{option.label}</span>
                {String(value) === String(option.value) && (
                  <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// --- SEARCHABLE STUDENT PORTAL DROPDOWN ---
function SearchableStudentDropdown({ value, options, onChange, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 240),
      });
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) updateCoords();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (isOpen) updateCoords();
    };
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".portal-student-dropdown")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={toggleDropdown}
        className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium ${
          disabled ? "opacity-50 cursor-not-allowed bg-zinc-100" : "cursor-pointer"
        }`}
      >
        <div className="flex items-center gap-2 truncate">
          <FiUser className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          <span className="truncate text-zinc-700">
            {selectedOption ? selectedOption.label : "-- Select Student --"}
          </span>
        </div>
        <FiChevronDown
          className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              minWidth: `${coords.width}px`,
            }}
            className="portal-student-dropdown fixed bg-white border border-zinc-200 rounded-lg shadow-xl p-2 z-[99999] animate-in fade-in zoom-in-95 duration-100 max-h-64 flex flex-col custom-scroll"
          >
            <div className="relative mb-2">
              <FiSearch className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search student..."
                className="w-full pl-8 pr-3 py-1 bg-zinc-50 border border-zinc-200 rounded-md text-xs focus:outline-none focus:border-zinc-400"
                autoFocus
              />
            </div>

            <div className="overflow-y-auto max-h-48 space-y-0.5">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-zinc-400 text-center">No students found</div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left transition-colors text-xs rounded-md cursor-pointer ${
                      String(value) === String(option.value)
                        ? "bg-zinc-100 text-zinc-900 font-semibold"
                        : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                    {String(value) === String(option.value) && (
                      <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// --- MAIN MODAL COMPONENT ---
export default function UniformInfractionModal({
  isOpen,
  onClose,
  onSave,
  infractionData = null,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingClassroomData, setLoadingClassroomData] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  // Fetched Collections
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolYearTerms, setSchoolYearTerms] = useState([]);
  const [parentAlerted, setParentAlerted] = useState(false);

  const INTERVENTION_OPTIONS = [
    { value: "verbal", label: "Verbal Warning" },
    { value: "telegram", label: "Telegram Parent Notice" },
    { value: "call", label: "Parent Phone Call" },
    { value: "detention", label: "Detention / Escalation" },
  ];

  const SPECIFIC_ITEM_OPTIONS = [
    { value: "No ID Card", label: "No ID Card" },
    { value: "No Tie", label: "No Tie" },
    { value: "No Scarf", label: "No Scarf" },
    { value: "No PE Uniform", label: "No PE Uniform" },
    { value: "Wrong Shoes", label: "Wrong Shoes" },
    { value: "Wrong Hair Color", label: "Wrong Hair Color" },
    { value: "Wrong ID String", label: "Wrong ID String" },
    { value: "No School Logo", label: "No School Logo" },
    { value: "Wrong Shirt Sleeves", label: "Wrong Shirt Sleeves" },
  ];

  // Helper function to format ISO date to Local datetime-local input string
  const getLocalDateTimeString = (dateInput) => {
    const d = dateInput ? new Date(dateInput) : new Date();
    if (isNaN(d.getTime())) return new Date().toISOString().slice(0, 16);
    const tzOffset = d.getTimezoneOffset() * 60000;
    const localISOTime = new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
    return localISOTime;
  };

  // Form State
  const [formData, setFormData] = useState({
    classroom: "",
    student: "",
    subject: "",
    specific_items: [],
    intervention_applied: "verbal",
    school_year_term: "",
    date_of_infraction: getLocalDateTimeString(),
    notes: "",
  });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Fetch initial data
  useEffect(() => {
    if (!isOpen) return;
    let isSubscribed = true;

    const fetchInitialData = async () => {
      setLoadingOptions(true);
      setError("");
      try {
        const [classroomsRes, termsRes] = await Promise.all([
          api.get("/academic/classroom/?page_size=100"),
          api.get("/year-terms/"),
        ]);

        if (!isSubscribed) return;

        const classroomsData = classroomsRes.data?.results || classroomsRes.data || [];
        const termsData = termsRes.data?.results || termsRes.data || [];

        setClassrooms(classroomsData);
        setSchoolYearTerms(termsData);

        if (termsData.length > 0 && !formData.school_year_term && !infractionData) {
          setFormData((prev) => ({ ...prev, school_year_term: String(termsData[0].id) }));
        }
      } catch (err) {
        if (isSubscribed) {
          console.error("Error fetching modal initial data:", err);
        }
      } finally {
        if (isSubscribed) setLoadingOptions(false);
      }
    };

    fetchInitialData();

    return () => {
      isSubscribed = false;
    };
  }, [isOpen]);

  // Handle classroom dynamics
  useEffect(() => {
    if (!formData.classroom) {
      setStudents([]);
      setSubjects([]);
      return;
    }

    let isSubscribed = true;

    const selectedClassroomObj = classrooms.find(
      (c) => String(c.id) === String(formData.classroom)
    );

    if (selectedClassroomObj && Array.isArray(selectedClassroomObj.subjects)) {
      setSubjects(selectedClassroomObj.subjects);
    } else {
      setSubjects([]);
    }

    const fetchClassroomStudents = async () => {
      setLoadingClassroomData(true);
      try {
        const studentsRes = await api.get(`/students/?classroom=${formData.classroom}&page_size=200`);
        if (isSubscribed) {
          const fetchedStudents = studentsRes.data?.results || studentsRes.data || [];
          setStudents(fetchedStudents);

          // If in edit mode and the student object from infractionData isn't in fetched list, append it so dropdown displays it cleanly
          if (infractionData && infractionData.student && typeof infractionData.student === "object") {
            const studentId = getId(infractionData.student);
            const exists = fetchedStudents.some((s) => String(s.id) === String(studentId));
            if (!exists) {
              setStudents((prev) => [...prev, infractionData.student]);
            }
          }
        }
      } catch (err) {
        if (isSubscribed) console.error("Error fetching classroom students:", err);
      } finally {
        if (isSubscribed) setLoadingClassroomData(false);
      }
    };

    fetchClassroomStudents();

    return () => {
      isSubscribed = false;
    };
  }, [formData.classroom, classrooms, infractionData]);

  // Populate form on Edit / Reset on Open
  useEffect(() => {
    if (!isOpen) return;

    if (infractionData) {
      const studentId = getId(infractionData.student || infractionData.student_id);
      const subjectId = getId(infractionData.subject);
      const termId = getId(infractionData.school_year_term);
      const classroomId = getId(infractionData.classroom || infractionData.classroom_id);

      let itemsArray = [];
      if (Array.isArray(infractionData.specific_items)) {
        itemsArray = infractionData.specific_items;
      } else if (infractionData.specific_item) {
        itemsArray = [infractionData.specific_item];
      }

      const rawNote = infractionData.detailed_note || infractionData.notes || "";
      const isParentAlerted = rawNote.includes("[PARENT ALERTED ADMINISTRATION]");
      const cleanNote = isParentAlerted
        ? rawNote.replace("[PARENT ALERTED ADMINISTRATION] ", "").trim()
        : rawNote;

      setParentAlerted(isParentAlerted);

      // Pre-seed classroom object if not yet loaded in classrooms list
      if (classroomId && infractionData.classroom && typeof infractionData.classroom === "object") {
        setClassrooms((prev) => {
          const exists = prev.some((c) => String(c.id) === String(classroomId));
          return exists ? prev : [...prev, infractionData.classroom];
        });
      }

      setFormData({
        classroom: classroomId,
        student: studentId,
        subject: subjectId,
        specific_items: itemsArray,
        intervention_applied: infractionData.intervention_applied || "verbal",
        school_year_term: termId,
        date_of_infraction: getLocalDateTimeString(infractionData.date_of_infraction),
        notes: cleanNote,
      });
    } else {
      setParentAlerted(false);
      setFormData({
        classroom: "",
        student: "",
        subject: "",
        specific_items: [],
        intervention_applied: "verbal",
        school_year_term: schoolYearTerms.length > 0 ? String(schoolYearTerms[0].id) : "",
        date_of_infraction: getLocalDateTimeString(),
        notes: "",
      });
    }
  }, [infractionData, isOpen]);

  if (!isOpen) return null;

  const handleToggleInfractionItem = (itemValue) => {
    setFormData((prev) => {
      const exists = prev.specific_items.includes(itemValue);
      return {
        ...prev,
        specific_items: exists
          ? prev.specific_items.filter((i) => i !== itemValue)
          : [...prev.specific_items, itemValue],
      };
    });
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!formData.student) {
      setError("Please select a student.");
      showToast("error", "Please select a student.");
      return;
    }

    if (formData.specific_items.length === 0) {
      setError("Please select at least one violation item.");
      showToast("error", "Please select at least one violation item.");
      return;
    }

    if (submitting) return;

    setError("");
    setSubmitting(true);

    try {
      const formattedNote = parentAlerted
        ? `[PARENT ALERTED ADMINISTRATION] ${formData.notes}`.trim()
        : formData.notes.trim();

      const basePayload = {
        classroom: formData.classroom ? parseInt(formData.classroom, 10) : null,
        student: parseInt(formData.student, 10),
        subject: formData.subject ? parseInt(formData.subject, 10) : null,
        school_year_term: formData.school_year_term ? parseInt(formData.school_year_term, 10) : null,
        date_of_infraction: new Date(formData.date_of_infraction).toISOString(),
        intervention_applied: formData.intervention_applied,
        notes: formattedNote,
        detailed_note: formattedNote,
      };

      let payload;
      if (infractionData) {
        payload = {
          ...basePayload,
          specific_item: formData.specific_items[0] || "Unspecified Item",
          specific_items: formData.specific_items,
        };
        await onSave(payload, infractionData.id);
      } else {
        payload = {
          ...basePayload,
          specific_items: formData.specific_items,
          specific_item: formData.specific_items.join(", "),
        };
        await onSave(payload, null);
      }

      showToast("success", "Uniform infraction saved successfully!");
      setTimeout(() => {
        onClose();
      }, 300);
    } catch (err) {
      console.error("Error submitting infraction form:", err);
      const errDetail = err.response?.data
        ? typeof err.response.data === "string"
          ? err.response.data
          : JSON.stringify(err.response.data)
        : "Failed to save infraction record.";
      setError(errDetail);
      showToast("error", errDetail);
    } finally {
      setSubmitting(false);
    }
  };

  const classroomOptions = [
    { value: "", label: "-- Select Classroom --" },
    ...classrooms.map((c) => ({
      value: String(c.id),
      label: c.display_name || c.name || (c.section ? c.section.name : `Classroom #${c.id}`),
    })),
  ];

  const studentOptions = students.map((s) => {
    const nameParts = [
      s.last_name || s.user?.last_name,
      s.first_name || s.user?.first_name,
    ].filter(Boolean);

    const displayName =
      nameParts.length > 0
        ? nameParts.join(", ")
        : s.full_name || s.name || s.user?.full_name || `Student #${s.id}`;

    const studentIdStr = s.student_id_number || s.student_id || s.id_number || "No ID";

    return {
      value: String(s.id),
      label: `${displayName} (${studentIdStr})`,
    };
  });

  const subjectOptions = [
    { value: "", label: "-- Select Subject (Optional) --" },
    ...subjects.map((sub) => ({
      value: String(sub.id),
      label:
        sub.subject_name ||
        sub.subject?.name ||
        sub.name ||
        `Subject Assignment #${sub.id}`,
    })),
  ];

  const termOptions = schoolYearTerms.map((t) => ({
    value: String(t.id),
    label: t.name || t.term?.name || `Term #${t.id}`,
  }));

  return (
    <>
      <ToastNotification toast={toast} onClose={() => setToast(null)} />
      {ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200 shadow-xl w-full max-w-lg overflow-visible">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 bg-zinc-50/50 rounded-t-xl">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-zinc-900 text-sm">
                  {infractionData ? "Edit Infraction Record" : "Log New Uniform Infraction"}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-zinc-400 hover:text-zinc-700 rounded-md transition-colors cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {error && (
                <div className="p-2.5 rounded-lg bg-red-50 border border-red-200/60 text-red-600 text-xs flex items-center gap-2">
                  <FiAlertCircle className="w-4 h-4 shrink-0" />
                  <span className="truncate">{error}</span>
                </div>
              )}

              {/* Classroom Selection */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">Classroom</label>
                <CustomDropdown
                  label="Select Classroom"
                  value={formData.classroom}
                  options={classroomOptions}
                  onChange={(val) =>
                    setFormData({
                      ...formData,
                      classroom: val,
                      student: "",
                      subject: "",
                    })
                  }
                  icon={FiGrid}
                  disabled={loadingOptions}
                />
              </div>

              {/* Student & Subject Filtered Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Student <span className="text-red-500">*</span>
                  </label>
                  <SearchableStudentDropdown
                    value={formData.student}
                    options={studentOptions}
                    onChange={(val) => setFormData({ ...formData, student: val })}
                    disabled={!formData.classroom || loadingClassroomData}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">Subject</label>
                  <CustomDropdown
                    value={formData.subject}
                    options={subjectOptions}
                    onChange={(val) => setFormData({ ...formData, subject: val })}
                    icon={FiBookOpen}
                    disabled={!formData.classroom || loadingClassroomData}
                  />
                </div>
              </div>

              {/* Multi-Select Violation Items */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  Violation Items (Select 1 or more) <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2 bg-zinc-50/80 p-2.5 border border-zinc-200/80 rounded-lg max-h-36 overflow-y-auto">
                  {SPECIFIC_ITEM_OPTIONS.map((item) => {
                    const checked = formData.specific_items.includes(item.value);
                    return (
                      <div
                        key={item.value}
                        onClick={() => handleToggleInfractionItem(item.value)}
                        className={`flex items-center gap-2 p-1.5 rounded-md border text-xs cursor-pointer select-none transition-colors ${
                          checked
                            ? "bg-zinc-900 text-white border-zinc-900"
                            : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-100"
                        }`}
                      >
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                            checked ? "bg-white border-white" : "border-zinc-300"
                          }`}
                        >
                          {checked && <FiCheck className="w-2.5 h-2.5 text-zinc-900" />}
                        </div>
                        <span className="truncate">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Intervention & Term Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Intervention Applied
                  </label>
                  <CustomDropdown
                    value={formData.intervention_applied}
                    options={INTERVENTION_OPTIONS}
                    onChange={(val) => setFormData({ ...formData, intervention_applied: val })}
                    icon={FiShield}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">School Term</label>
                  <CustomDropdown
                    value={formData.school_year_term}
                    options={
                      termOptions.length > 0
                        ? termOptions
                        : [{ value: "", label: "No Terms Available" }]
                    }
                    onChange={(val) => setFormData({ ...formData, school_year_term: val })}
                    icon={FiClock}
                    disabled={loadingOptions}
                  />
                </div>
              </div>

              {/* Date & Time */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.date_of_infraction}
                  onChange={(e) => setFormData({ ...formData, date_of_infraction: e.target.value })}
                  className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1">Notes / Context</label>
                <textarea
                  rows="2"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Provide any additional details..."
                  className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3.5 py-1.5 bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg text-xs font-medium transition-colors shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {submitting && <FiRefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {submitting ? "Saving..." : infractionData ? "Update Record" : "Log Infraction"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}